/**
 * Hook centralisé pour la détection responsive.
 *
 * Breakpoints :
 *  - compact : téléphone, portrait OU paysage
 *              (largeur < 640px, ou paysage avec hauteur ≤ 520px)
 *  - medium  : tablette portrait (641–1024px, hauteur > 520px)
 *  - wide    : tablette paysage / desktop (> 1024px)
 */

import { useSyncExternalStore, useCallback } from 'react';

export type Breakpoint = 'compact' | 'medium' | 'wide';
export type Orientation = 'portrait' | 'landscape';

interface ResponsiveState {
  breakpoint: Breakpoint;
  orientation: Orientation;
  isCompact: boolean;
  isMedium: boolean;
  isWide: boolean;
  isPortrait: boolean;
  isLandscape: boolean;
}

const BP_COMPACT = 640;
const BP_MEDIUM = 1024;
const PHONE_LANDSCAPE_MAX_HEIGHT = 520;

const mqCompactWidth = typeof window !== 'undefined'
  ? window.matchMedia(`(max-width: ${BP_COMPACT - 1}px)`)
  : null;

const mqPhoneLandscape = typeof window !== 'undefined'
  ? window.matchMedia(`(orientation: landscape) and (max-height: ${PHONE_LANDSCAPE_MAX_HEIGHT}px)`)
  : null;

const mqWide = typeof window !== 'undefined'
  ? window.matchMedia(`(min-width: ${BP_MEDIUM + 1}px)`)
  : null;

const mqPortrait = typeof window !== 'undefined'
  ? window.matchMedia('(orientation: portrait)')
  : null;

function applyDomAttrs(state: ResponsiveState) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-bp', state.breakpoint);
  document.documentElement.setAttribute('data-orientation', state.orientation);
  const notchSide = state.isLandscape ? resolveNotchSide() : '';
  if (notchSide) document.documentElement.setAttribute('data-notch-side', notchSide);
  else document.documentElement.removeAttribute('data-notch-side');
}

/**
 * Côté de l'encoche en paysage.
 * iOS reporte souvent le même inset gauche ET droite : on ne s'en sert que
 * s'ils sont nettement différents. Sinon l'angle de rotation :
 *   90° (sens antihoraire) → encoche à gauche
 *   270° / -90° (sens horaire) → encoche à droite
 */
function resolveNotchSide(): 'left' | 'right' | '' {
  if (typeof window === 'undefined') return '';

  let left = 0;
  let right = 0;
  if (document.body) {
    const probe = document.createElement('div');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;padding-left:env(safe-area-inset-left,0px);padding-right:env(safe-area-inset-right,0px)';
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    left = parseFloat(cs.paddingLeft) || 0;
    right = parseFloat(cs.paddingRight) || 0;
    probe.remove();
  }

  const gap = 12;
  if (left > right + gap) return 'left';
  if (right > left + gap) return 'right';

  const rawAngle = screen.orientation?.angle
    ?? (typeof window.orientation === 'number' ? window.orientation : NaN);
  const angle = ((Number(rawAngle) % 360) + 360) % 360;
  if (angle === 90) return 'left';
  if (angle === 270) return 'right';

  const type = screen.orientation?.type;
  if (type === 'landscape-primary') return 'left';
  if (type === 'landscape-secondary') return 'right';

  if (left > 0 && left >= right) return 'left';
  if (right > 0) return 'right';
  return '';
}

function getSnapshot(): ResponsiveState {
  if (typeof window === 'undefined') {
    return {
      breakpoint: 'wide',
      orientation: 'landscape',
      isCompact: false,
      isMedium: false,
      isWide: true,
      isPortrait: false,
      isLandscape: true,
    };
  }

  const isCompact = mqCompactWidth!.matches || mqPhoneLandscape!.matches;
  const isWide = !isCompact && mqWide!.matches;
  const isMedium = !isCompact && !isWide;
  const isPortrait = mqPortrait!.matches;

  let breakpoint: Breakpoint;
  if (isCompact) breakpoint = 'compact';
  else if (isWide) breakpoint = 'wide';
  else breakpoint = 'medium';

  return {
    breakpoint,
    orientation: isPortrait ? 'portrait' : 'landscape',
    isCompact,
    isMedium,
    isWide,
    isPortrait,
    isLandscape: !isPortrait,
  };
}

let cachedSnapshot = getSnapshot();
applyDomAttrs(cachedSnapshot);

if (typeof requestAnimationFrame === 'function') {
  requestAnimationFrame(() => {
    cachedSnapshot = getSnapshot();
    applyDomAttrs(cachedSnapshot);
  });
}

function subscribe(callback: () => void): () => void {
  const handler = () => {
    cachedSnapshot = getSnapshot();
    applyDomAttrs(cachedSnapshot);
    callback();
  };

  mqCompactWidth?.addEventListener('change', handler);
  mqPhoneLandscape?.addEventListener('change', handler);
  mqWide?.addEventListener('change', handler);
  mqPortrait?.addEventListener('change', handler);
  window.addEventListener('orientationchange', handler);
  screen.orientation?.addEventListener('change', handler);
  window.visualViewport?.addEventListener('resize', handler);

  return () => {
    mqCompactWidth?.removeEventListener('change', handler);
    mqPhoneLandscape?.removeEventListener('change', handler);
    mqWide?.removeEventListener('change', handler);
    mqPortrait?.removeEventListener('change', handler);
    window.removeEventListener('orientationchange', handler);
    screen.orientation?.removeEventListener('change', handler);
    window.visualViewport?.removeEventListener('resize', handler);
  };
}

function getServerSnapshot(): ResponsiveState {
  return {
    breakpoint: 'wide',
    orientation: 'landscape',
    isCompact: false,
    isMedium: false,
    isWide: true,
    isPortrait: false,
    isLandscape: true,
  };
}

export function useResponsive(): ResponsiveState {
  return useSyncExternalStore(subscribe, useCallback(() => cachedSnapshot, []), getServerSnapshot);
}
